/**
 * One-way repeated-measures (within-subjects) ANOVA, with Mauchly's test of sphericity and
 * the Greenhouse–Geisser and Huynh–Feldt corrections.
 *
 * With n subjects measured under k conditions:
 *
 *   SS_cond  = n Σ (x̄ⱼ − x̄)²            df = k − 1
 *   SS_subj  = k Σ (x̄ᵢ − x̄)²            df = n − 1
 *   SS_error = SS_total − SS_cond − SS_subj   df = (n − 1)(k − 1)
 *   F = MS_cond / MS_error
 *
 * — the F test of `summary(aov(y ~ cond + Error(subj/cond)))` in R. Effect sizes: partial
 * η² = SS_cond / (SS_cond + SS_error) and generalized η² (Olejnik & Algina 2003; the
 * "ges" of the ez package) = SS_cond / (SS_cond + SS_subj + SS_error).
 *
 * Sphericity: the variances of all pairwise differences between conditions are equal. Let
 * C be any (k − 1) × k matrix of orthonormal contrasts and M = C S Cᵀ the contrast-space
 * covariance matrix (S is the k × k sample covariance matrix of the conditions). Then
 *
 *   Mauchly's W = det(M) / (tr(M) / (k − 1))^(k − 1)
 *   Greenhouse–Geisser ε = tr(M)² / ((k − 1) tr(M²))                       (Box 1954)
 *   Huynh–Feldt ε̃ = (n(k − 1)ε − 2) / ((k − 1)(n − 1 − (k − 1)ε)), at most 1  (1976)
 *
 * W is referred to χ² by R's `mauchly.test`, including its second-order term; the corrected
 * F tests multiply both degrees of freedom by ε. All of these are pinned against R's
 * `mauchly.test(lm(Y ~ 1), X = ~1)` and `anova(lm(Y ~ 1), X = ~1, test = "Spherical")`.
 *
 * Pairwise follow-ups are paired t-tests on the n differences (which do not rely on
 * sphericity), with Bonferroni and Holm adjustments.
 */

import { mean, variance } from "@/lib/stats/descriptive";
import {
  chiSquareUpper,
  fUpper,
  studentTTwoTailed,
} from "@/lib/stats/distributions";
import {
  bonferroniAdjust,
  holmAdjust,
} from "@/lib/tool-kit/stats-tests-more/adjust";

export interface Sphericity {
  /** Mauchly's W; null for two conditions (always spherical) or a singular covariance. */
  w: number | null;
  chiSquare: number | null;
  df: number | null;
  p: number | null;
  epsilonGG: number;
  epsilonHF: number;
  /** 1 / (k − 1): the smallest ε can be. */
  epsilonLower: number;
}

export interface CorrectedTest {
  epsilon: number;
  df1: number;
  df2: number;
  p: number;
}

export interface RmPairwise {
  first: number;
  second: number;
  meanDifference: number;
  se: number;
  t: number;
  df: number;
  p: number;
  pBonferroni: number;
  pHolm: number;
  /** Cohen's d_z = mean difference / SD of differences. */
  dz: number;
}

export interface RepeatedMeasuresAnova {
  n: number;
  k: number;
  means: number[];
  sds: number[];
  grandMean: number;
  ssConditions: number;
  ssSubjects: number;
  ssError: number;
  ssTotal: number;
  dfConditions: number;
  dfSubjects: number;
  dfError: number;
  msConditions: number;
  msSubjects: number;
  msError: number;
  f: number;
  p: number;
  partialEtaSquared: number;
  generalizedEtaSquared: number;
  sphericity: Sphericity;
  greenhouseGeisser: CorrectedTest;
  huynhFeldt: CorrectedTest;
  pairs: RmPairwise[];
}

export type RmAnovaFailure =
  | "too-few-subjects"
  | "too-few-conditions"
  | "no-error-variance";

export type RmAnovaOutcome =
  | { ok: true; result: RepeatedMeasuresAnova }
  | { ok: false; reason: RmAnovaFailure };

/* -------------------------------------------------------------- linear algebra */

type Matrix = number[][];

/** The k × k sample covariance matrix of the columns of `rows`. */
function covarianceMatrix(rows: readonly (readonly number[])[]): Matrix {
  const n = rows.length;
  const k = rows[0].length;
  const centres = Array.from({ length: k }, (_, j) =>
    mean(rows.map((row) => row[j]))
  );
  const s: Matrix = Array.from({ length: k }, () =>
    new Array<number>(k).fill(0)
  );
  for (let a = 0; a < k; a++) {
    for (let b = a; b < k; b++) {
      let sum = 0;
      for (const row of rows) {
        sum += (row[a] - centres[a]) * (row[b] - centres[b]);
      }
      s[a][b] = sum / (n - 1);
      s[b][a] = s[a][b];
    }
  }
  return s;
}

/** Orthonormal Helmert contrasts: (k − 1) rows, each summing to zero. */
function helmert(k: number): Matrix {
  return Array.from({ length: k - 1 }, (_, i) => {
    const r = i + 1;
    const scale = Math.sqrt(r * (r + 1));
    return Array.from({ length: k }, (_, j) => {
      if (j < r) {
        return 1 / scale;
      }
      return j === r ? -r / scale : 0;
    });
  });
}

/** C S Cᵀ. */
function contrastCovariance(s: Matrix): Matrix {
  const c = helmert(s.length);
  const cs = c.map((row) =>
    s[0].map((_, col) => row.reduce((acc, v, m) => acc + v * s[m][col], 0))
  );
  return cs.map((row) =>
    c.map((crow) => row.reduce((acc, v, m) => acc + v * crow[m], 0))
  );
}

/**
 * log det of a symmetric positive semi-definite matrix by Gaussian elimination, or null
 * if it is singular. A positive-definite matrix needs no pivoting: every pivot is positive.
 */
function logDeterminant(matrix: Matrix): number | null {
  const a = matrix.map((row) => [...row]);
  const size = a.length;
  const floor = 1e-12 * Math.max(1, Math.abs(matrix[0][0]));
  let logDet = 0;
  for (let col = 0; col < size; col++) {
    const pivot = a[col][col];
    if (!(pivot > floor)) {
      return null;
    }
    logDet += Math.log(pivot);
    for (let row = col + 1; row < size; row++) {
      const factor = a[row][col] / pivot;
      for (let m = col; m < size; m++) {
        a[row][m] -= factor * a[col][m];
      }
    }
  }
  return logDet;
}

/* ------------------------------------------------------------------ sphericity */

/** R's `mauchly.test` p-value, with its second-order correction term. */
function mauchlyP(
  logW: number,
  k: number,
  nSubjects: number
): {
  chiSquare: number;
  df: number;
  p: number;
} {
  const pp = k - 1;
  const nu = nSubjects - 1;
  const rho = 1 - (2 * pp * pp + pp + 2) / (6 * pp * nu);
  // R writes `3 * p` (the number of conditions) in this coefficient, not `3 * pp`.
  const w2 =
    ((pp + 2) * (pp - 1) * (pp - 2) * (2 * pp ** 3 + 6 * pp * pp + 3 * k + 2)) /
    (288 * (nu * pp * rho) ** 2);
  const chiSquare = -nu * rho * logW;
  const df = (pp * (pp + 1)) / 2 - 1;
  const p1 = chiSquareUpper(chiSquare, df);
  const p2 = chiSquareUpper(chiSquare, df + 4);
  return { chiSquare, df, p: Math.min(1, Math.max(0, p1 + w2 * (p2 - p1))) };
}

function sphericityOf(rows: readonly (readonly number[])[]): Sphericity {
  const n = rows.length;
  const k = rows[0].length;
  const pp = k - 1;
  const epsilonLower = 1 / pp;
  if (k === 2) {
    return {
      w: null,
      chiSquare: null,
      df: null,
      p: null,
      epsilonGG: 1,
      epsilonHF: 1,
      epsilonLower,
    };
  }
  const m = contrastCovariance(covarianceMatrix(rows));
  const trace = m.reduce((acc, row, i) => acc + row[i], 0);
  const traceSquare = m.reduce(
    (acc, row) => acc + row.reduce((a, v) => a + v * v, 0),
    0
  );
  const epsilonGG = Math.min(
    1,
    Math.max(epsilonLower, trace ** 2 / (pp * traceSquare))
  );
  const denominator = pp * (n - 1 - pp * epsilonGG);
  const epsilonHF =
    denominator > 0 ? Math.min(1, (n * pp * epsilonGG - 2) / denominator) : 1;
  const logDet = logDeterminant(m);
  if (logDet === null || n - 1 < pp) {
    return {
      w: null,
      chiSquare: null,
      df: null,
      p: null,
      epsilonGG,
      epsilonHF: Math.max(epsilonLower, epsilonHF),
      epsilonLower,
    };
  }
  const logW = logDet - pp * Math.log(trace / pp);
  return {
    w: Math.exp(logW),
    ...mauchlyP(logW, k, n),
    epsilonGG,
    epsilonHF: Math.max(epsilonLower, epsilonHF),
    epsilonLower,
  };
}

/* -------------------------------------------------------------------- pairwise */

function pairwiseTests(rows: readonly (readonly number[])[]): RmPairwise[] {
  const n = rows.length;
  const k = rows[0].length;
  const pairs: RmPairwise[] = [];
  for (let a = 0; a < k; a++) {
    for (let b = a + 1; b < k; b++) {
      const diffs = rows.map((row) => row[a] - row[b]);
      const meanDifference = mean(diffs);
      const sd = Math.sqrt(variance(diffs));
      const se = sd / Math.sqrt(n);
      let t = meanDifference / se;
      let p = studentTTwoTailed(t, n - 1);
      if (sd === 0) {
        t =
          meanDifference === 0
            ? 0
            : Math.sign(meanDifference) * Number.POSITIVE_INFINITY;
        p = meanDifference === 0 ? 1 : 0;
      }
      pairs.push({
        first: a,
        second: b,
        meanDifference,
        se,
        t,
        df: n - 1,
        p,
        pBonferroni: 0,
        pHolm: 0,
        dz: sd === 0 ? Number.NaN : meanDifference / sd,
      });
    }
  }
  const raw = pairs.map((pair) => pair.p);
  const bonferroni = bonferroniAdjust(raw);
  const holm = holmAdjust(raw);
  for (const [index, pair] of pairs.entries()) {
    pair.pBonferroni = bonferroni[index];
    pair.pHolm = holm[index];
  }
  return pairs;
}

/* ------------------------------------------------------------------------ main */

function corrected(
  f: number,
  df1: number,
  df2: number,
  epsilon: number
): CorrectedTest {
  return {
    epsilon,
    df1: df1 * epsilon,
    df2: df2 * epsilon,
    p: fUpper(f, df1 * epsilon, df2 * epsilon),
  };
}

/** `rows` has one array per subject and one entry per condition. */
export function repeatedMeasuresAnova(
  rows: readonly (readonly number[])[]
): RmAnovaOutcome {
  const n = rows.length;
  const k = rows[0]?.length ?? 0;
  if (n < 2) {
    return { ok: false, reason: "too-few-subjects" };
  }
  if (k < 2) {
    return { ok: false, reason: "too-few-conditions" };
  }
  const columns = Array.from({ length: k }, (_, j) =>
    rows.map((row) => row[j])
  );
  const means = columns.map((column) => mean(column));
  const grandMean = mean(rows.flat());
  const ssConditions =
    n * means.reduce((acc, m) => acc + (m - grandMean) ** 2, 0);
  const ssSubjects =
    k * rows.reduce((acc, row) => acc + (mean(row) - grandMean) ** 2, 0);
  const ssTotal = rows.flat().reduce((acc, x) => acc + (x - grandMean) ** 2, 0);
  const ssError = Math.max(0, ssTotal - ssConditions - ssSubjects);
  if (ssError <= 1e-12 * Math.max(1, ssTotal)) {
    return { ok: false, reason: "no-error-variance" };
  }
  const dfConditions = k - 1;
  const dfSubjects = n - 1;
  const dfError = dfSubjects * dfConditions;
  const msConditions = ssConditions / dfConditions;
  const msError = ssError / dfError;
  const f = msConditions / msError;
  const sphericity = sphericityOf(rows);
  return {
    ok: true,
    result: {
      n,
      k,
      means,
      sds: columns.map((column) => Math.sqrt(variance(column))),
      grandMean,
      ssConditions,
      ssSubjects,
      ssError,
      ssTotal,
      dfConditions,
      dfSubjects,
      dfError,
      msConditions,
      msSubjects: ssSubjects / dfSubjects,
      msError,
      f,
      p: fUpper(f, dfConditions, dfError),
      partialEtaSquared: ssConditions / (ssConditions + ssError),
      generalizedEtaSquared:
        ssConditions / (ssConditions + ssSubjects + ssError),
      sphericity,
      greenhouseGeisser: corrected(
        f,
        dfConditions,
        dfError,
        sphericity.epsilonGG
      ),
      huynhFeldt: corrected(f, dfConditions, dfError, sphericity.epsilonHF),
      pairs: pairwiseTests(rows),
    },
  };
}
